import { Toast } from "@base-ui/react/toast";
import { CheckCircle2Icon, InfoIcon, XCircleIcon, XIcon } from "lucide-react";
import type { ComponentType } from "react";
import { toastManager } from "@/lib/toast";
import { cn } from "@/lib/utils";

const TYPE_ICONS: Record<string, ComponentType<{ className?: string }>> = {
  success: CheckCircle2Icon,
  error: XCircleIcon,
  info: InfoIcon,
};

function ToastList() {
  const { toasts } = Toast.useToastManager();

  return (
    <Toast.Portal>
      <Toast.Viewport className="fixed right-4 bottom-4 z-[100] flex w-[calc(100%-2rem)] max-w-sm flex-col gap-2 outline-none sm:right-6 sm:bottom-6">
        {toasts.map((t) => {
          const Icon = TYPE_ICONS[t.type ?? "info"] ?? InfoIcon;
          return (
            <Toast.Root
              key={t.id}
              toast={t}
              className={cn(
                "relative flex items-start gap-2 rounded-lg border bg-popover p-3 pr-8 text-popover-foreground shadow-lg transition-all",
                "data-[starting-style]:translate-x-full data-[starting-style]:opacity-0",
                "data-[ending-style]:translate-x-full data-[ending-style]:opacity-0",
                "data-[type=success]:border-success/80 data-[type=success]:bg-success/90 data-[type=success]:text-success-foreground",                                                          
                "data-[type=error]:border-destructive/80 data-[type=error]:bg-destructive/90 data-[type=error]:text-destructive-foreground", 
              )}
            >
              <Toast.Content className="flex flex-1 items-start gap-2">
                <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <div className="flex flex-col gap-0.5">
                  <Toast.Title className="text-sm font-medium">{t.title}</Toast.Title>
                  {t.description ? <Toast.Description className="text-xs opacity-90">{t.description}</Toast.Description> : null}
                </div>
              </Toast.Content>
              <Toast.Close
                aria-label="Dismiss"
                className="absolute top-2 right-2 cursor-pointer rounded-xs opacity-60 outline-none hover:opacity-100 focus-visible:ring-2 focus-visible:ring-ring"
              >
                <XIcon className="size-3.5" />
              </Toast.Close>
            </Toast.Root>
          );
        })}
      </Toast.Viewport>
    </Toast.Portal>
  );
}

/**
 * Mounted once near the root (`app/providers.tsx`). Call
 * `toast.success()`/`toast.error()`/`toast.info()` from `@/lib/toast`
 * anywhere — a plain event handler, a mutation's `onSuccess`/`onError`,
 * outside any component — to show one; nothing here needs to be imported
 * at the call site.
 */
export function Toaster() {
  return (
    <Toast.Provider toastManager={toastManager}>
      <ToastList />
    </Toast.Provider>
  );
}
